import type { WorkspaceDesk } from '@/config/workspace-desk'
import type { StaffNavContext } from './staff-nav-context'

/** Shared baseline: hide all nav sections on restricted desks. */
const DESK_OFF: Partial<StaffNavContext> = {
  showJobPostsNav: false,
  showApplicantsNav: false,
  showTalentPoolNav: false,
  showRecruitmentSection: false,
  walkInListedUnderRecruitment: false,
  showWalkInNav: false,
  showVisaOnboardingNav: false,
  showOnboardingFollowUpNav: false,
  showWorkforceDirectory: false,
  showEmployeeCompleteness: false,
  showStaffDeployment: false,
  showDailyAttendance: false,
  showStaffRoster: false,
  showClientDirectory: false,
  showPurgedEmployees: false,
  showStaffing: false,
  showTeam: false,
  showAccessControl: false,
  showPayroll: false,
  showPayrollAdmin: false,
  showAccountsWorkspace: false,
  showFinanceNav: false,
  showInvoicingNav: false,
  showTeamAttendance: false,
  showCoordinatorAttendance: false,
  showCoordinatorReview: false,
  showAttendanceIssues: false,
  showAttendanceNotifications: false,
  showTeamAvailability: false,
  showSupervisorStaffDocs: false,
  showSupervisorBulkHiring: false,
  showHiringRequisitions: false,
  showHiringRequisitionsHire: false,
  showSupervisorAppSignIns: false,
  showSelfAttendanceReview: false,
  showAdminSearch: false,
  showClientIdentityNav: false,
  showAdminContracts: false,
  showAccountsContracts: false,
  showSalesNav: false,
  showStaffOperations: false,
  showContactInquiries: false,
  showClientApplicants: false,
  showProOnboarding: false,
  showProExit: false,
  showProWorkforceDirectory: false,
  showProDeskAdmin: false,
  showAdminNav: false,
  showDocumentFilingAdmin: false,
  showLeavesNav: false,
  showDesktopActivityNav: false,
  showMyDesktopActivityNav: false,
  showStocksNav: false,
  showIssueLimitsNav: false,
  showStockApprovalsNav: false,
  showNotificationRulesNav: false,
  showAttendanceAlertsNav: false,
  showSupervisorMaterialRequest: false,
  showSupervisorGatePass: false,
  showAdminGatePass: false,
  showHrGatePass: false,
  showStaffPersonalDetailsNav: false,
  showStaffPersonalDetailsImportNav: false,
  showInterviewIntakeNav: false,
  isClientOnly: false,
  showAccountSection: false,
  showMyAccessNav: false,
}

export type DeskNavProfileOptions = {
  keepAccountSection?: boolean
}

export type DeskNavProfile = {
  mode: 'full' | 'restricted'
  buildPatch?: (ctx: StaffNavContext) => Partial<StaffNavContext>
  options?: DeskNavProfileOptions
}

function restricted(
  buildPatch: (ctx: StaffNavContext) => Partial<StaffNavContext>,
  options?: DeskNavProfileOptions,
): DeskNavProfile {
  const profile: DeskNavProfile = { mode: 'restricted', buildPatch }
  if (options) profile.options = options
  return profile
}

/**
 * Desk subdomain → sidebar IA focus. Permissions still gate each leaf;
 * profiles only hide whole sections for the hostname persona.
 * Restricted desks keep groups (no flatten) so Team / Operations stay nested.
 */
export const WORKSPACE_DESK_NAV_PROFILES: Record<WorkspaceDesk, DeskNavProfile> = {
  admin: { mode: 'full' },
  /** Multi-role home: capability-gated full nav (union of modules the session allows). */
  app: { mode: 'full' },
  sales: restricted((ctx) => ({
    ...DESK_OFF,
    showSalesNav: ctx.showSalesNav,
    // Manage Clients lives under Sales on this desk.
    showClientIdentityNav: ctx.showClientIdentityNav,
    showAdminContracts: ctx.showAdminContracts,
    showAdminNav: false,
  })),
  operations: { mode: 'full' },
  onboarding: restricted((ctx) => ({
    ...DESK_OFF,
    showWalkInNav: false,
    showVisaOnboardingNav: ctx.showVisaOnboardingNav,
    showWorkforceDirectory: ctx.showWorkforceDirectory,
    showEmployeeCompleteness: false,
    showStaffPersonalDetailsNav: false,
    showStaffPersonalDetailsImportNav: false,
    showInterviewIntakeNav: ctx.showInterviewIntakeNav,
    showHrGatePass: ctx.showHrGatePass,
    showDesktopActivityNav: ctx.showDesktopActivityNav,
  })),
  recruiter: restricted((ctx) => ({
    ...DESK_OFF,
    showJobPostsNav: ctx.showJobPostsNav,
    showApplicantsNav: ctx.showApplicantsNav,
    showTalentPoolNav: ctx.showTalentPoolNav,
    showHiringRequisitionsHire: ctx.showHiringRequisitionsHire,
    showRecruitmentSection:
      ctx.showJobPostsNav ||
      ctx.showApplicantsNav ||
      ctx.showTalentPoolNav ||
      ctx.showHiringRequisitionsHire,
    showTeamAttendance: ctx.showTeamAttendance,
    showAttendanceIssues: ctx.showTeamAttendance,
    showAttendanceNotifications: false,
    showLeavesNav: ctx.showLeavesNav,
  })),
  reception: restricted((ctx) => {
    const showWalkIn = ctx.showWalkInNav
    const showTalent = ctx.showTalentPoolNav || showWalkIn
    return {
      ...DESK_OFF,
      showTalentPoolNav: showTalent,
      showRecruitmentSection: showTalent,
      showWalkInNav: showWalkIn,
      // Keep walk-in under Recruitment so reception is a single group.
      walkInListedUnderRecruitment: showWalkIn && showTalent,
    }
  }),
  pro: restricted(
    (ctx) => ({
      ...DESK_OFF,
      showProOnboarding: ctx.showProOnboarding,
      showProExit: ctx.showProExit,
      showProWorkforceDirectory: ctx.showProWorkforceDirectory,
      showProDeskAdmin: ctx.showProDeskAdmin,
      showDocumentFilingSubmit: ctx.showDocumentFilingSubmit,
    }),
    { keepAccountSection: true },
  ),
  hr: restricted((ctx) => {
    const showWalkIn = ctx.showWalkInNav
    // Do not treat walk-in as Talent Pool — HR directory staff should not open
    // recruitment screens unless they hold real talent/applicant capabilities.
    const showTalent = ctx.showTalentPoolNav
    return {
      ...DESK_OFF,
      // Directory + attendance oversight (HR sits between employees and supervisors).
      // Walk-in stays available under the HR group when not paired with talent pool.
      showTalentPoolNav: showTalent,
      showRecruitmentSection: showTalent,
      showWalkInNav: showWalkIn,
      walkInListedUnderRecruitment: showWalkIn && showTalent,
      showWorkforceDirectory: ctx.showWorkforceDirectory,
      showEmployeeCompleteness: ctx.showEmployeeCompleteness,
      showStaffDeployment: ctx.showStaffDeployment,
      showVisaOnboardingNav: false,
      // Follow-up queues on the HR desk (not the full Visa & Onboarding hub).
      showOnboardingFollowUpNav: ctx.showVisaOnboardingNav,
      showStaffPersonalDetailsNav: false,
      showStaffPersonalDetailsImportNav: false,
      showInterviewIntakeNav: ctx.showInterviewIntakeNav,
      showDocumentFilingSubmit: ctx.showDocumentFilingSubmit,
      // HR chase list only — no mark-sheet Attendance, no Mobile app directory.
      showTeamAttendance: false,
      showCoordinatorReview: ctx.showCoordinatorReview,
      showAttendanceIssues: ctx.showTeamAttendance,
      showAttendanceNotifications: ctx.showAttendanceNotifications,
      showLeavesNav: ctx.showLeavesNav,
      showHrGatePass: ctx.showHrGatePass,
      showTeamAvailability: false,
      // Self-attendance stays capability-flagged; Team section hides the leaf for now.
      showSelfAttendanceReview: ctx.showSelfAttendanceReview,
      showPayroll: false,
      showPayrollAdmin: false,
    }
  }),
  supervisor: restricted(
    (ctx) => ({
      ...DESK_OFF,
      showTeamAttendance: ctx.showTeamAttendance,
      showCoordinatorAttendance: ctx.showCoordinatorAttendance,
      showCoordinatorReview: ctx.showCoordinatorReview,
      showWorkforceDirectory: ctx.showWorkforceDirectory,
      showStaffDeployment: ctx.showStaffDeployment,
      showDailyAttendance: ctx.showDailyAttendance,
      showStaffRoster: ctx.showStaffRoster,
      showClientDirectory: ctx.showClientDirectory,
      showAttendanceIssues: ctx.showTeamAttendance,
      showAttendanceNotifications: false,
      showTeamAvailability: false,
      showSupervisorAppSignIns: ctx.showTeamAttendance,
      showSupervisorStaffDocs: ctx.showSupervisorStaffDocs,
      showSupervisorBulkHiring: ctx.showSupervisorBulkHiring,
      showHiringRequisitions: ctx.showHiringRequisitions,
      showSupervisorMaterialRequest: ctx.showSupervisorMaterialRequest,
      showSupervisorGatePass: ctx.showSupervisorGatePass,
      showAdminGatePass: ctx.showAdminGatePass,
      showJobPostsNav: ctx.showJobPostsNav,
      showApplicantsNav: ctx.showApplicantsNav,
      showTalentPoolNav: ctx.showTalentPoolNav,
      showHiringRequisitionsHire: ctx.showHiringRequisitionsHire,
      showRecruitmentSection:
        ctx.showJobPostsNav ||
        ctx.showApplicantsNav ||
        ctx.showTalentPoolNav ||
        ctx.showHiringRequisitionsHire,
      showDocumentFilingSubmit: false,
      showStocksNav: ctx.showStocksNav,
      showStockApprovalsNav: ctx.showStockApprovalsNav,
      showStaffPersonalDetailsImportNav: false,
      showAccountSection: false,
      showMyAccessNav: false,
      // Enrolled supervisors need their own day board on this desk (Account is hidden).
      showMyDesktopActivityNav: ctx.showMyDesktopActivityNav,
    }),
  ),
  finance: restricted(
    (ctx) => ({
      ...DESK_OFF,
      showAccountsWorkspace: ctx.showAccountsWorkspace,
      showAccountsContracts: ctx.showAccountsContracts,
      showFinanceNav: ctx.showFinanceNav,
      showInvoicingNav: ctx.showInvoicingNav,
      showPayroll: false,
      showPayrollAdmin: false,
      showTeamAttendance: false,
    }),
    { keepAccountSection: true },
  ),
  client: restricted((ctx) => ({
    ...DESK_OFF,
    showClientApplicants: ctx.showClientApplicants,
    isClientOnly: ctx.isClientOnly,
  })),
}

export function mergeDeskNavProfile(
  ctx: StaffNavContext,
  desk: WorkspaceDesk,
): StaffNavContext {
  const profile = WORKSPACE_DESK_NAV_PROFILES[desk]
  if (profile.mode === 'full') {
    return ctx
  }

  const patch = profile.buildPatch?.(ctx) ?? DESK_OFF
  const keepAccount = profile.options?.keepAccountSection ?? false

  return {
    ...ctx,
    ...patch,
    showAccountSection: keepAccount,
    showMyAccessNav: keepAccount ? ctx.showMyAccessNav : false,
    showMyDesktopActivityNav: keepAccount
      ? ctx.showMyDesktopActivityNav
      : Boolean(patch.showMyDesktopActivityNav),
    showSupervisorMaterialRequest: ctx.showSupervisorMaterialRequest,
    // HR host uses the HR menu. An administrator session must not keep the admin shell here.
    ...(desk === 'hr' ? { isThinAdmin: false } : {}),
  }
}
